import { Component, OnInit } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';

import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Clipboard } from '@angular/cdk/clipboard';

import { ModuloEnum } from '../../../../shared/enums/usuario/modulo.enum';
import { FuncionalidadeEnum } from '../../../../shared/enums/usuario/funcionalidade.enum';
import { AlertService, AlertType } from '../../../../shared/services/alert.service';
import { requiredTrim } from '../../../../shared/validators/string-trim.validator';

import { EmissorService } from '../../../../shared/services/apis/dfe/emissor.service';
import { EmissorRequest } from '../../../../shared/services/requests/dfe/emissor.request';
import { GetEmissorByIdResponse } from '../../../../shared/services/responses/dfe/get-emissor-by-id.response';
import { SalvarEmissorResponse } from '../../../../shared/services/responses/dfe/salvar-emissor.response';
import { Emissor } from '../../../../shared/services/models/dfe/emissor.model';
import { ConfirmModalComponent } from '../../../../shared/components/confirm-modal/confirm-modal.component';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { FooterActionComponent } from '../../../../shared/components/footer-action/footer-action.component';
import { MatSelectModule } from '@angular/material/select';
import { HeaderComponent } from '../../../../shared/components/header/header.component';
import { AtivacaoSectionCardComponent } from '../../../../shared/components/ativacao-section-card/ativacao-section-card.component';
import { AlertCardComponent } from '@shared/components/alert-card/alert-card.component';
import { TipoAmbienteEnum } from '@shared/enums/dfe/tipo-ambiente.enum';
import { ConfiguracaoDFeGeralService } from '@shared/services/apis/dfe/configuracao-dfe-geral.service';
import { NaturezaOperacaoService } from '@shared/services/apis/motor-fiscal/natureza-operacao.service';
import {
  ReatribuirNaturezasEmissorDialogComponent,
  ReatribuirNaturezasEmissorDialogData,
} from '../emissor-lista/dialogs/reatribuir-naturezas-emissor-dialog/reatribuir-naturezas-emissor-dialog.component';

/**
 * Escolha do campo Ambiente na tela. O banco grava só Produção/Homologação em `idTipoAmbiente`;
 * Automático é a marcação `isTipoAmbienteAutomatico`, com o ambiente atual da configuração geral.
 */
enum OpcaoAmbienteEmissorEnum {
  Automatico = 0,
  Producao = TipoAmbienteEnum.Producao,
  Homologacao = TipoAmbienteEnum.Homologacao,
}

@Component({
  selector: 'app-emissor-cadastro',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    MatInputModule,
    MatFormFieldModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatSlideToggleModule,
    MatTooltipModule,
    MatDialogModule,
    FooterActionComponent,
    MatSelectModule,
    HeaderComponent,
    AtivacaoSectionCardComponent,
    AlertCardComponent,
  ],
  templateUrl: './emissor-cadastro.component.html',
  styleUrls: ['./emissor-cadastro.component.css'],
})
export class EmissorCadastroComponent implements OnInit {
  public moduloEnum = ModuloEnum.Configuracoes;
  public funcionalidadeEnum = FuncionalidadeEnum.Configuracoes_NotasFiscais;
  public model: FormGroup;
  public _isLoading = false;
  public idEmissor: number | null = null;
  public readonly OpcaoAmbienteEmissorEnum = OpcaoAmbienteEmissorEnum;
  /** Ambiente atual da configuração geral: é o que o emissor em Automático passa a gravar. */
  public ambienteConfiguracaoGeral: TipoAmbienteEnum | null = null;
  /** Emissor como veio da API, para comparar ambiente, status e padrão antes de salvar. */
  private emissorCarregado: Emissor | null = null;
  /** A série de NF-e já tem número reservado ou utilizado: fica fixa, outra série é outro emissor. */
  public isSerieNFeComNumeracao = false;

  private readonly descricaoAmbiente: Record<number, string> = {
    [TipoAmbienteEnum.Producao]: 'Produção',
    [TipoAmbienteEnum.Homologacao]: 'Homologação',
  };

  constructor(
    private formBuilder: FormBuilder,
    private emissorService: EmissorService,
    private alertService: AlertService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private dialog: MatDialog,
    private clipboard: Clipboard,
    private configuracaoDFeGeralService: ConfiguracaoDFeGeralService,
    private naturezaOperacaoService: NaturezaOperacaoService
  ) {
    this.model = this.formBuilder.group({
      idEmissor: new FormControl({ value: '', disabled: true }),
      token: new FormControl({ value: '', disabled: true }),
      nome: ['', [Validators.required, requiredTrim, Validators.maxLength(30)]],
      descricao: ['', [Validators.maxLength(60)]],
      ativo: [true, Validators.required],
      emiteNFCe: [false],
      ultimoSequencialNFCe: [null],
      serieNFCe: [null],
      emiteNFe: [false],
      ultimoSequencialNFe: [null],
      serieNFe: [null],
      regerarToken: [false, Validators.required],
      // Novo emissor nasce em Automático; o usuário pode fixar Homologação ou Produção.
      opcaoAmbiente: [OpcaoAmbienteEmissorEnum.Automatico, Validators.required],
      isPadraoNFe: [false],
    });
  }

  ngOnInit(): void {
    const idParam = this.activatedRoute.snapshot.paramMap.get('idEmissor');
    if (idParam) {
      this.idEmissor = parseInt(idParam, 10);
      this.popularDadosEmissor();
    }

    this.atualizarEstadoCamposNFCe();
    this.atualizarEstadoCamposNFe();
    this.carregarAmbienteConfiguracaoGeral();
    this.model.get('ativo')?.valueChanges.subscribe(() => this.atualizarEstadoPadrao());
  }

  private carregarAmbienteConfiguracaoGeral(): void {
    this.configuracaoDFeGeralService.get().subscribe({
      next: response => {
        if (response.isError) return;
        this.ambienteConfiguracaoGeral = response.configuracaoDFeGeral?.idTipoAmbiente ?? null;
      },
      error: () => (this.ambienteConfiguracaoGeral = null),
    });
  }

  // ── Ambiente ──────────────────────────────────────────────────────────────

  public get isAutomaticoSelecionado(): boolean {
    return this.model.get('opcaoAmbiente')?.value === OpcaoAmbienteEmissorEnum.Automatico;
  }

  /** "Automático (Homologação)" quando o ambiente da configuração geral é conhecido. */
  public get descricaoOpcaoAutomatico(): string {
    const ambiente = this.ambienteConfiguracaoGeral != null ? this.descricaoAmbiente[this.ambienteConfiguracaoGeral] : null;
    return ambiente ? `Automático (${ambiente})` : 'Automático';
  }

  /** Ambiente que o emissor vai gravar: o fixo escolhido ou, em Automático, o da configuração geral. */
  private get ambienteEfetivo(): TipoAmbienteEnum | null {
    return this.isAutomaticoSelecionado
      ? this.ambienteConfiguracaoGeral
      : (this.model.get('opcaoAmbiente')?.value as TipoAmbienteEnum);
  }

  /** O aviso vale para quem passa a acompanhar a configuração geral com NFC-e ativa. */
  public get exibirAvisoNFCeAutomatico(): boolean {
    return this.isAutomaticoSelecionado
      && !!this.model.get('emiteNFCe')?.value
      && !this.emissorCarregado?.isTipoAmbienteAutomatico;
  }

  // ── Padrão para NF-e ──────────────────────────────────────────────────────

  /** Motivo da marcação Padrão para NF-e estar indisponível, ou null quando pode ser alterada. */
  public get motivoPadraoIndisponivel(): string | null {
    if (this.emissorCarregado?.isPadraoNFe) {
      return 'Este já é o emissor padrão para NF-e. Para trocar, marque outro emissor como padrão.';
    }
    if (!this.model.get('ativo')?.value) return 'Disponível apenas para emissores ativos.';
    return null;
  }

  /**
   * Emissor inativo não pode ser marcado. O padrão carregado também fica travado: ele só deixa
   * de ser o padrão quando outro emissor é marcado, para a empresa nunca ficar sem padrão.
   */
  private atualizarEstadoPadrao(): void {
    const isPadraoNFe = this.model.get('isPadraoNFe');
    if (this.motivoPadraoIndisponivel) {
      if (!this.emissorCarregado?.isPadraoNFe) isPadraoNFe?.setValue(false, { emitEvent: false });
      isPadraoNFe?.disable({ emitEvent: false });
    } else {
      isPadraoNFe?.enable({ emitEvent: false });
    }
  }

  onToggleEmiteNFCe(): void {
    this.atualizarEstadoCamposNFCe();
  }

  onToggleEmiteNFe(): void {
    this.atualizarEstadoCamposNFe();
  }

  private atualizarEstadoCamposNFCe(): void {
    const emiteNFCe = this.model.get('emiteNFCe')?.value;
    const sequencial = this.model.get('ultimoSequencialNFCe');
    const serie = this.model.get('serieNFCe');

    if (emiteNFCe) {
      sequencial?.enable();
      serie?.enable();
      sequencial?.setValidators([Validators.required, Validators.min(0)]);
      serie?.setValidators([Validators.required, Validators.min(1)]);
    } else {
      sequencial?.disable();
      serie?.disable();
      sequencial?.clearValidators();
      serie?.clearValidators();
    }
    sequencial?.updateValueAndValidity();
    serie?.updateValueAndValidity();
  }

  private atualizarEstadoCamposNFe(): void {
    const emiteNFe = this.model.get('emiteNFe')?.value;
    const sequencial = this.model.get('ultimoSequencialNFe');
    const serie = this.model.get('serieNFe');

    if (emiteNFe) {
      sequencial?.enable();
      if (this.isSerieNFeComNumeracao) serie?.disable();
      else serie?.enable();
      sequencial?.setValidators([Validators.required, Validators.min(0)]);
      serie?.setValidators([Validators.required, Validators.min(1)]);
    } else {
      sequencial?.disable();
      serie?.disable();
      sequencial?.clearValidators();
      serie?.clearValidators();
    }
    sequencial?.updateValueAndValidity();
    serie?.updateValueAndValidity();
  }

  popularDadosEmissor(): void {
    if (!this.idEmissor) return;

    this._isLoading = true;
    this.emissorService.getById(this.idEmissor).subscribe({
      next: (response: GetEmissorByIdResponse) => {
        this._isLoading = false;
        if (response.isError) {
          this.alertService.show('Erro', response.errorDescription, AlertType.error);
          this.router.navigate(['/configuracoes/notas-fiscais/emissor']);
          return;
        }

        this.emissorCarregado = response.emissor;
        this.isSerieNFeComNumeracao = response.isSerieNFeComNumeracao;
        this.model.patchValue(response.emissor);
        this.model.get('opcaoAmbiente')?.setValue(
          response.emissor.isTipoAmbienteAutomatico
            ? OpcaoAmbienteEmissorEnum.Automatico
            : (response.emissor.idTipoAmbiente as number as OpcaoAmbienteEmissorEnum)
        );
        this.atualizarEstadoCamposNFCe();
        this.atualizarEstadoCamposNFe();
        this.atualizarEstadoPadrao();
      },
      error: error => {
        this._isLoading = false;
        this.alertService.show(
          'Erro inesperado',
          'Não foi possível carregar os dados do emissor.',
          AlertType.error
        );
        this.router.navigate(['/configuracoes/notas-fiscais/emissor']);
      },
    });
  }

  async submit(): Promise<void> {
    if (this._isLoading) return;

    if (this.model.invalid) {
      this.alertService.show(
        'Atenção',
        'Por favor, preencha todos os campos obrigatórios.',
        AlertType.warning
      );
      return;
    }

    const formValue = this.model.getRawValue();
    const acaoDependenteDaNFe = this.obterAcaoQueRetiraNFe(formValue.ativo, formValue.emiteNFe);

    // O padrão prevalece: sem outro emissor marcado, nem a reatribuição de naturezas é oferecida.
    if (acaoDependenteDaNFe && this.emissorCarregado?.isPadraoNFe) {
      this.alertService.show(
        'Atenção',
        `Marque outro emissor como padrão para NF-e antes de ${acaoDependenteDaNFe}.`,
        AlertType.warning
      );
      return;
    }

    if (!(await this.confirmarTrocaAmbienteEfetivo(formValue))) return;
    if (acaoDependenteDaNFe && !(await this.reatribuirNaturezasDoEmissor(acaoDependenteDaNFe))) return;

    this._isLoading = true;
    const emiteNFe = Boolean(formValue.emiteNFe);
    const request: EmissorRequest = {
      nome: formValue.nome,
      descricao: formValue.descricao,
      ativo: formValue.ativo,
      emiteNFCe: formValue.emiteNFCe,
      sequencialNFCe: formValue.ultimoSequencialNFCe !== null && formValue.ultimoSequencialNFCe !== ''
        ? Number(formValue.ultimoSequencialNFCe)
        : null,
      serieNFCe: formValue.serieNFCe ? Number(formValue.serieNFCe) : null,
      emiteNFe: emiteNFe,
      sequencialNFe: formValue.ultimoSequencialNFe !== null && formValue.ultimoSequencialNFe !== ''
        ? Number(formValue.ultimoSequencialNFe)
        : null,
      serieNFe: formValue.serieNFe ? Number(formValue.serieNFe) : null,
      regerarToken: Boolean(formValue.regerarToken),
      isTipoAmbienteAutomatico: this.isAutomaticoSelecionado,
      // Em Automático a API grava o ambiente atual da configuração geral e ignora este valor;
      // o fallback só cobre a configuração geral não ter carregado na tela.
      idTipoAmbiente: this.ambienteEfetivo ?? this.emissorCarregado?.idTipoAmbiente ?? TipoAmbienteEnum.Homologacao,
      isPadraoNFe: Boolean(formValue.isPadraoNFe) && Boolean(formValue.ativo) && emiteNFe,
    };

    if (this.idEmissor) {
      this.atualizar(request);
    } else {
      this.criar(request);
    }
  }

  /**
   * Ação da edição que tira o emissor da emissão de NF-e, no texto da mensagem de bloqueio.
   * Null na inclusão ou quando nada disso acontece.
   */
  private obterAcaoQueRetiraNFe(ativo: boolean, emiteNFe: boolean): string | null {
    if (!this.emissorCarregado) return null;
    if (this.emissorCarregado.ativo && !ativo) return 'inativar este emissor';
    if (this.emissorCarregado.emiteNFe && !emiteNFe) return 'desativar a NF-e deste emissor';
    return null;
  }

  /** O último sequencial não muda com o ambiente: o usuário confirma sabendo de onde a numeração segue. */
  private async confirmarTrocaAmbienteEfetivo(formValue: any): Promise<boolean> {
    const ambienteAnterior = this.emissorCarregado?.idTipoAmbiente ?? null;
    const ambienteNovo = this.ambienteEfetivo;
    if (ambienteAnterior == null || ambienteNovo == null || ambienteAnterior === ambienteNovo) return true;

    const sequenciais: string[] = [];
    if (formValue.emiteNFe) sequenciais.push(`${formValue.ultimoSequencialNFe ?? 0} na NF-e`);
    if (formValue.emiteNFCe) sequenciais.push(`${formValue.ultimoSequencialNFCe ?? 0} na NFC-e`);

    const paragrafoNumeracao = sequenciais.length
      ? `A numeração deste emissor continuará a partir de ${sequenciais.join(' e ')}. Se esta é a primeira emissão neste ambiente, confira o último sequencial.`
      : '';

    const dialogRef = this.dialog.open(ConfirmModalComponent, {
      data: {
        title: 'Troca de ambiente',
        innerHtml: `Este emissor passa de ${this.descricaoAmbiente[ambienteAnterior]} para ${this.descricaoAmbiente[ambienteNovo]}.`
          + (paragrafoNumeracao ? `<br><br>${paragrafoNumeracao}` : ''),
        buttonConfirm: 'Confirmar',
        buttonClose: 'Cancelar',
      },
    });

    const resultado = await firstValueFrom(dialogRef.afterClosed());
    return !!resultado?.confirm;
  }

  /**
   * Naturezas que apontam para o emissor precisam ser reatribuídas antes de ele sair da emissão
   * de NF-e. Devolve true quando não há naturezas ou quando a reatribuição foi gravada.
   */
  private async reatribuirNaturezasDoEmissor(descricaoAcao: string): Promise<boolean> {
    if (!this.idEmissor) return true;

    try {
      const response = await firstValueFrom(this.naturezaOperacaoService.getPorEmissor(this.idEmissor));
      if (response.isError) {
        this.alertService.show('Erro', response.errorDescription, AlertType.error);
        return false;
      }

      const naturezas = response.naturezas ?? [];
      if (naturezas.length === 0) return true;

      const dialogRef = this.dialog.open<ReatribuirNaturezasEmissorDialogComponent, ReatribuirNaturezasEmissorDialogData, boolean>(
        ReatribuirNaturezasEmissorDialogComponent,
        {
          width: '640px',
          maxWidth: '95vw',
          data: {
            idEmissor: this.idEmissor,
            nomeEmissor: this.emissorCarregado?.nome ?? this.model.get('nome')?.value,
            descricaoAcao,
            naturezas,
          },
        }
      );
      return !!(await firstValueFrom(dialogRef.afterClosed()));
    } catch {
      this.alertService.show(
        'Erro inesperado',
        'Não foi possível verificar as naturezas de operação que usam este emissor.',
        AlertType.error
      );
      return false;
    }
  }

  private mensagemSucesso(response: SalvarEmissorResponse, mensagemPadrao: string): string {
    if (!response.nomeEmissorPadraoAnterior) return mensagemPadrao;
    return `${mensagemPadrao} "${this.model.get('nome')?.value}" passou a ser o emissor padrão para NF-e no lugar de "${response.nomeEmissorPadraoAnterior}".`;
  }

  private criar(request: EmissorRequest): void {
    this.emissorService.create(request).subscribe({
      next: (response: SalvarEmissorResponse) => {
        this._isLoading = false;
        if (response.isError) {
          this.alertService.show('Erro', response.errorDescription, AlertType.error);
          return;
        }
        this.alertService.show('Sucesso', this.mensagemSucesso(response, 'Emissor criado com sucesso!'), AlertType.success);
        this.router.navigate(['/configuracoes/notas-fiscais/emissor']);
      },
      error: error => {
        this._isLoading = false;
        this.alertService.show('Erro inesperado', error, AlertType.error);
      },
    });
  }

  private atualizar(request: EmissorRequest): void {
    if (!this.idEmissor) return;

    this.emissorService.update(this.idEmissor, request).subscribe({
      next: (response: SalvarEmissorResponse) => {
        this._isLoading = false;
        if (response.isError) {
          this.alertService.show('Erro', response.errorDescription, AlertType.error);
          return;
        }
        this.alertService.show('Sucesso', this.mensagemSucesso(response, 'Emissor atualizado com sucesso!'), AlertType.success);
        this.router.navigate(['/configuracoes/notas-fiscais/emissor']);
      },
      error: error => {
        this._isLoading = false;
        this.alertService.show('Erro inesperado', error, AlertType.error);
      },
    });
  }

  cancelar(): void {
    this.router.navigate(['/configuracoes/notas-fiscais/emissor']);
  }

  modalConfirmChangeToken(): void {
    const dialogRef = this.dialog.open(ConfirmModalComponent, {
      data: {
        title: 'Atenção',
        innerHtml: `Você tem certeza que deseja regerar a token? </br> </br>
                Caso confirme todas as integrações que utilizam dessa token irão paralizar até que seja atualizada para a nova token gerada.
                </br>
                </br>
                Essa ação só surtirá efeito, caso confirmada, no momento em que as alterações da tela forem salvas!`,
        buttonConfirm: 'Confirmar',
        buttonClose: 'Cancelar',
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      const confirmou = !!result?.confirm;
      this.model.get('regerarToken')?.setValue(confirmou);
      this.model.get('regerarToken')?.updateValueAndValidity();

      if (confirmou) {
        this.alertService.show('Atenção', 'A regeneração do token será aplicada ao salvar.', AlertType.info);
      }
    });
  }

  copyToken(): void {
    const token = this.model.getRawValue()?.token;

    if (!token) {
      this.alertService.show('Atenção', 'Nenhum token disponível para copiar.', AlertType.warning);
      return;
    }

    const copied = this.clipboard.copy(token);
    if (copied) {
      this.alertService.show('Sucesso', 'Token copiado para a área de transferência.', AlertType.success);
      return;
    }

    this.alertService.show('Erro', 'Não foi possível copiar o token.', AlertType.error);
  }

  onSequencialInput(campo: string, valor: string): void {
    const apenasNumeros = valor.replace(/\D/g, '').slice(0, 8);
    if (apenasNumeros !== this.model.get(campo)?.value?.toString()) {
      this.model.get(campo)?.setValue(apenasNumeros, { emitEvent: false });
    }
  }

  onSerieInput(campo: string, valor: string): void {
    const apenasNumeros = valor.replace(/\D/g, '').slice(0, 3);
    if (apenasNumeros !== this.model.get(campo)?.value?.toString()) {
      this.model.get(campo)?.setValue(apenasNumeros, { emitEvent: false });
    }
  }
}
